import type { LibraryRecord } from "../../../server/core/library";
import { list } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import type { ScreenContext } from "../../shell/types";
import { autosaveStatus, createAutosave } from "../../ui/autosave";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal, type Modal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { archiveModal, area, field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { setupBar, setupQuery } from "../../ui/setup-bar";

// Settings › Requirements library (brief 1B-2, mockup settings/library): every requirement a
// title, client, unit or module can ask for (document, attestation or verification; whether it
// expires; restricted results), the profiles that say who asks for what (each item blocking or
// not, with buffer days), and the Maryland Board of Nursing titles. Changes save on their own a
// moment after the last one, and everyone's readiness is worked out again.

type View = LibraryRecord;
type Res = { ok: boolean; status: number; body: View & { error?: string } };
type Type = View["types"][number];
type Profile = View["profiles"][number];

const KINDS = [
  ["document", "Document"],
  ["attestation", "Attestation"],
  ["verification", "Verification"],
] as const;
const LEVELS = [
  ["blocking", "Blocking"],
  ["non_blocking", "Not blocking"],
] as const;

let seq = 0;
function check(label: string, on: boolean, hint?: string) {
  const id = `lb${++seq}`;
  const input = el("input", { type: "checkbox", id, checked: on });
  return {
    input,
    el: el(
      "div",
      { class: "field wide" },
      el("label", { for: id, class: "row", style: "gap:8px;align-items:center" }, input, label),
      hint ? el("span", { class: "hint" }, hint) : null,
    ),
  };
}

export async function libraryScreen(host: HTMLElement, ctx: ScreenContext) {
  const page = el("div", { class: "record" });
  host.append(page);
  // Brief 1E-7: the module you came from plus Shared, or the picker's.
  const q = () => setupQuery("library");
  const first = (await api<View>("GET", `/api/library${q()}`)) as Res;
  if (!first.ok) {
    page.append(
      pageHead(ctx),
      el("p", { class: "note", role: "alert" }, "The library couldn't be loaded."),
    );
    return;
  }
  let view = first.body;

  async function took(res: Res): Promise<Res> {
    if (res.ok) {
      view = res.body;
      if (page.isConnected) draw();
    }
    return res;
  }
  const send = async (method: string, path: string, body?: unknown) =>
    took((await api<View>(method, `/api/library${path}${q()}`, body)) as Res);

  function autosaving(m: Modal, inputs: HTMLElement[], put: () => Promise<Res>) {
    const status = autosaveStatus();
    m.root.querySelector(".cardhead")?.after(el("div", { style: "margin:-4px 0 4px" }, status.el));
    const saver = createAutosave({
      save: async () => {
        const res = await put();
        if (res.ok) m.note.hidden = true;
        else if (res.status === 400 || res.status === 409) refuse(m.note, res.body.error);
        return res.ok ? { ok: true } : { ok: false, error: res.body.error };
      },
      onStatus: status.show,
    });
    for (const i of inputs) {
      i.addEventListener("input", () => saver.changed());
      i.addEventListener("change", () => saver.changed());
    }
  }

  // ---- Requirements -------------------------------------------------------------------------------
  function typeFields(t?: Type) {
    const name = field("Name", t?.name, { maxlength: "120" });
    const kind = select("Kind", KINDS, t?.kind ?? "document");
    const renews = field("Renews", t?.renews ?? "Once", { maxlength: "60" });
    const src = field("Checked against", t?.checkedAgainst, { maxlength: "160" });
    const variants = area("Variants clients can choose", t?.variants);
    const expires = check(
      "Expires",
      t?.expires ?? false,
      "An approval then needs its expiry date.",
    );
    const restricted = check(
      "Restricted result",
      t?.restricted ?? false,
      "Background checks and drug screens: only people who can see background results see the result and the file; others see the status.",
    );
    const afterHire = check(
      "Only after hire",
      t?.afterHire ?? false,
      "Never asked of a candidate (I-9).",
    );
    const level = select("Usually", LEVELS, t?.defaultLevel ?? "blocking");
    // Brief 1E-30: whether the caregiver sees it in the field app.
    const shown = select(
      "Field app",
      [
        ["shown", "Shown to the caregiver"],
        ["file", "On file only"],
      ],
      (t?.shownToCaregiver ?? true) ? "shown" : "file",
    );
    return {
      grid: el(
        "div",
        { class: "formgrid" },
        el("div", { class: "wide" }, name.el),
        kind.el,
        level.el,
        renews.el,
        src.el,
        el(
          "span",
          { class: "hint wide", style: "margin:-4px 0 0" },
          "Document: a file is collected. Attestation: the person signs. Verification: a source is checked.",
        ),
        variants.el,
        expires.el,
        restricted.el,
        afterHire.el,
        shown.el,
      ),
      focus: () => name.input.focus(),
      inputs: [
        name.input,
        kind.input,
        renews.input,
        src.input,
        variants.input,
        expires.input,
        restricted.input,
        afterHire.input,
        level.input,
        shown.input,
      ],
      values: () => ({
        name: name.input.value,
        kind: kind.input.value,
        renews: renews.input.value,
        checkedAgainst: src.input.value,
        variants: variants.input.value,
        expires: expires.input.checked,
        restricted: restricted.input.checked,
        afterHire: afterHire.input.checked,
        shownToCaregiver: shown.input.value === "shown",
        defaultLevel: level.input.value,
      }),
    };
  }

  function addType() {
    const f = typeFields();
    const add = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("plus"),
      " Add requirement",
    );
    const m = openModal({
      title: "Add requirement",
      sub: "Then put it on the profiles that ask for it.",
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid)],
      actions: [add],
      wide: true,
    });
    f.focus();
    add.addEventListener("click", async () => {
      add.disabled = true;
      const res = await send("POST", "/types", f.values());
      add.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast(`${f.values().name.trim()} added`);
    });
  }

  function editType(t: Type) {
    const f = typeFields(t);
    const m = openModal({
      title: t.name,
      sub: `${t.kindLabel} · ${t.expires ? "Expires" : "Does not expire"} · renews ${t.renews}. Changes save as they're made.`,
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid)],
      wide: true,
    });
    if (!view.canEdit) {
      for (const i of f.inputs) i.setAttribute("disabled", "");
      return;
    }
    autosaving(m, f.inputs, () => send("PUT", `/types/${t.id}`, f.values()));
    const arch = el("button", { type: "button", class: "btn sm ghost" }, "Archive requirement");
    arch.addEventListener("click", () => {
      m.close();
      archiveModal(t.name, `/api/library/types/${t.id}/archive`, () => send("GET", ""));
    });
    m.root.append(el("div", { class: "actions" }, arch));
  }

  // ---- Profiles ----------------------------------------------------------------------------------
  function newProfile() {
    const owners: [string, string][] = [
      ...view.titles.map((t): [string, string] => [`title|${t.code}`, `Title: ${t.code}`]),
      ...view.owners.modules.map((m): [string, string] => [`module|${m.id}`, `Module: ${m.name}`]),
      ...view.owners.clients.flatMap((c): [string, string][] => [
        [`client|${c.id}`, `Client: ${c.name}`],
        ...c.units.map((u): [string, string] => [`unit|${u.id}`, `Unit: ${c.name} · ${u.name}`]),
      ]),
    ].filter(([v]) => !view.profiles.some((p) => `${p.ownerKind}|${p.ownerRef}` === v));
    const owner = select("Whose requirements", owners, owners[0]?.[0] ?? "");
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " New profile");
    const m = openModal({
      title: "New profile",
      sub: "A client's and a unit's requirements add to the title's and the module's.",
      body: [el("div", { class: "form", style: "max-width:none" }, owner.el)],
      actions: [add],
    });
    owner.input.focus();
    add.addEventListener("click", async () => {
      const [ownerKind, ownerRef] = owner.input.value.split("|");
      add.disabled = true;
      const had = new Set(view.profiles.map((p) => p.id));
      const res = await send("POST", "/profiles", { ownerKind, ownerRef, items: [] });
      add.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      const made = view.profiles.find((p) => !had.has(p.id));
      if (made) editProfile(made);
    });
  }

  function editProfile(p: Profile) {
    const name = field("Name", p.name, { maxlength: "120" });
    const titleItem = p.items.find((i) => "title" in i);
    const title = select(
      "Needs title",
      [
        ["", "No title needed"],
        ...view.titles.map((t) => [t.code, `${t.code} or a title that meets it`] as const),
      ],
      titleItem && "title" in titleItem ? titleItem.title : "",
    );
    const rows = view.types.map((t) => {
      const item = p.items.find((i) => "type" in i && i.type === t.code);
      const asked = el("input", {
        type: "checkbox",
        checked: !!item,
        "aria-label": `Asks for ${t.name}`,
        disabled: !view.canEdit,
      });
      const level = el(
        "select",
        { "aria-label": `Level for ${t.name}`, disabled: !view.canEdit },
        ...LEVELS.map(([v, txt]) =>
          el("option", { value: v, selected: v === (item?.level ?? t.defaultLevel) }, txt),
        ),
      );
      const buffer = el("input", {
        type: "number",
        min: "0",
        max: "365",
        value: String(item && "bufferDays" in item ? item.bufferDays : 14),
        "aria-label": `Buffer days for ${t.name}`,
        style: "width:5em",
        disabled: !view.canEdit,
      });
      return { t, asked, level, buffer };
    });
    const m = openModal({
      title: p.name,
      sub: `${p.ownerLabel}: ${p.ownerName}. Changes save as they're made.`,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "formgrid" }, name.el, title.el),
        ),
        table(
          [
            { h: "Asks", f: (r) => r.asked },
            {
              h: "Requirement",
              f: (r) =>
                el(
                  "span",
                  {},
                  el("b", {}, r.t.name),
                  el("div", { class: "small muted" }, r.t.kindLabel),
                ),
            },
            { h: "Level", f: (r) => r.level },
            { h: "Buffer days", f: (r) => r.buffer },
          ],
          rows,
          undefined,
          "Requirements in this profile",
        ),
      ],
      wide: true,
    });
    if (!view.canEdit) {
      name.input.disabled = true;
      title.input.disabled = true;
      return;
    }
    autosaving(
      m,
      [name.input, title.input, ...rows.flatMap((r) => [r.asked, r.level, r.buffer])],
      () =>
        send("PUT", `/profiles/${p.id}`, {
          name: name.input.value,
          items: [
            ...(title.input.value ? [{ title: title.input.value, level: "blocking" }] : []),
            ...rows
              .filter((r) => r.asked.checked)
              .map((r) => ({
                type: r.t.code,
                level: r.level.value,
                bufferDays: Number(r.buffer.value || "14"),
              })),
          ],
        }),
    );
    const arch = el("button", { type: "button", class: "btn sm ghost" }, "Archive profile");
    arch.addEventListener("click", () => {
      m.close();
      archiveModal(p.name, `/api/library/profiles/${p.id}/archive`, () => send("GET", ""));
    });
    m.root.append(el("div", { class: "actions" }, arch));
  }

  // ---- Drawing -----------------------------------------------------------------------------------
  function draw() {
    const add = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("plus"),
      " Add requirement",
    );
    add.addEventListener("click", addType);
    // Brief 1E-32: the requirements one page at a time, with search.
    const ask = (params: URLSearchParams) => {
      const shown = new URLSearchParams(q().replace(/^\?/, ""));
      for (const [k, v] of params) shown.set(k, v);
      return api<PageInfo & { requirements: Type[] }>(
        "GET",
        `/api/library/requirements?${shown.toString()}`,
      );
    };
    const requirements = list<Type>({
      id: "settings/library",
      userId: ctx.me.user.id,
      noun: "requirements",
      source: async (params) => {
        const r = await ask(params);
        return r.ok ? { rows: r.body.requirements, page: r.body } : null;
      },
      rowId: (t) => t.id,
      columns: [
        {
          id: "name",
          label: "Requirement",
          text: (t) => t.name,
          cell: (t) => el("b", {}, t.name),
          search: true,
        },
        { id: "kind", label: "Type", text: (t) => t.kindLabel, search: true },
        { id: "expires", label: "Expires", text: (t) => (t.expires ? "Yes" : "No") },
        { id: "renews", label: "Renews", text: (t) => t.renews ?? "" },
        {
          id: "checked",
          label: "Checked against",
          text: (t) => t.checkedAgainst ?? "",
          search: true,
          cell: (t) =>
            el(
              "span",
              {},
              t.checkedAgainst || "–",
              t.restricted ? el("div", {}, chip("Restricted result", "warn")) : null,
              t.shownToCaregiver ? null : el("div", {}, chip("On file only")),
            ),
        },
        {
          id: "used",
          label: "Used in",
          num: true,
          text: (t) => `${t.usedIn} profile${t.usedIn === 1 ? "" : "s"}`,
        },
      ],
      onOpen: editType,
      empty: "No requirements yet.",
    });

    const profiles = card(
      "Who asks for what",
      "Each title, module, client or unit's own list.",
      view.canEdit ? [ib("plus", "New profile", newProfile)] : [],
      table<Profile>(
        [
          { h: "Profile", f: (p) => el("b", {}, p.name) },
          {
            h: "For",
            f: (p) => el("span", {}, chip(p.ownerLabel), " ", p.ownerName),
          },
          {
            h: "Blocking",
            f: (p) =>
              el(
                "span",
                { class: "small" },
                p.items
                  .filter((i) => i.level === "blocking")
                  .map((i) => i.words)
                  .join(", ") || "–",
              ),
          },
          {
            h: "Items",
            num: true,
            f: (p) => String(p.items.length),
          },
        ],
        view.profiles,
        editProfile,
        "Profiles",
      ),
    );

    const titles = el(
      "section",
      { class: "card" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, "Maryland Board of Nursing · since 04/01/2026"),
          el("h2", {}, "Professional titles"),
        ),
      ),
      el(
        "p",
        { class: "small muted", style: "margin:0 0 8px" },
        "Only these titles are used on screens, job posts and requirements.",
      ),
      table(
        [
          { h: "Title", f: (t) => el("b", {}, t.code) },
          { h: "What it is", f: (t) => t.whatItIs },
          { h: "Where it can work", f: (t) => t.whereItWorks },
          { h: "To renew", f: (t) => t.toRenew },
        ],
        view.titles,
        undefined,
        "Professional titles",
      ),
    );

    page.replaceChildren(
      pageHead(
        ctx,
        "Every item a client can ask for. Open one to set its rules.",
        view.canEdit ? [add] : [],
        null,
      ),
      setupBar("library", "Requirements", view.picker, () => void send("GET", "")),
      el("section", { class: "card", "aria-label": "Requirements" }, requirements),
      profiles,
      view.types.length ? titles : muted(""),
    );
  }

  draw();
}
